import { useCallback, useEffect, RefObject } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { GridApi } from 'ag-grid-community';

const COLUMNS_TO_SAVE_ON_FOCUS_LOST = ['quantity', 'unitType', 'unitPrice', 'comments'] as const;
type FocusSaveColumn = typeof COLUMNS_TO_SAVE_ON_FOCUS_LOST[number];

const isColumnWithFocusSave = (colId: string): colId is FocusSaveColumn =>
  COLUMNS_TO_SAVE_ON_FOCUS_LOST.includes(colId as FocusSaveColumn);

const getEditingFocusSaveCell = (api: GridApi) =>
  api.getEditingCells().find(cell => isColumnWithFocusSave(cell.column.getColId()));

export const useFocusSaveOnCellEdit = (gridRef: RefObject<AgGridReact>) => {
  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (!gridRef.current) return;

    const editingFocusSaveCell = getEditingFocusSaveCell(gridRef.current.api);
    if (!editingFocusSaveCell) return;

    const target = event.target as HTMLElement;
    const isClickInsideGridCell = target.closest('.ag-root') && target.closest('.ag-cell');

    if (!isClickInsideGridCell) {
      gridRef.current.api.stopEditing();
    }
  }, [gridRef]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  const onCellFocusChanged = useCallback(() => {
    if (!gridRef.current) return;

    const editingFocusSaveCell = getEditingFocusSaveCell(gridRef.current.api);
    if (!editingFocusSaveCell) return;

    const focusedCell = gridRef.current.api.getFocusedCell();
    const isFocusOnSameCell =
      focusedCell &&
      isColumnWithFocusSave(focusedCell.column.getColId()) &&
      focusedCell.rowIndex === editingFocusSaveCell.rowIndex;

    if (!isFocusOnSameCell) {
      gridRef.current.api.stopEditing();
    }
  }, [gridRef]);

  return { onCellFocusChanged };
};
